/**
 * ⚠️  INTERNAL USE ONLY — NOT FOR PUBLIC ACCESS
 * Route: /admin/edit-site
 * Password-gated. noindex. Not linked from any public page.
 *
 * Renders real production page components inside an EditModeContext so that
 * ImageSlot components show edit overlays. No duplicate components — the same
 * source used by the public site is rendered here.
 */
import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AdminLoginGate, isAdminAuthed } from '../../components/AdminLoginGate';
import SEOHead from '../../components/SEOHead';
import ImageEditor from '../../components/ImageEditor';
import { EditModeContext } from '../../contexts/EditModeContext';
import {
  saveImageSlot,
  uploadImageFile,
  invalidateSlotCache,
} from '../../lib/image-slots-client';
import type { ImageSlot } from '../../lib/image-slots-client';
import {
  saveContentSlot,
  deleteContentSlot,
} from '../../lib/content-slots-client';
import type { ContentType } from '../../lib/content-slots-client';
import { Monitor, Tablet, Smartphone, ChevronLeft } from '../../lib/icons';
import MediaLibraryPanel from '../../components/MediaLibraryPanel';
import ContentEditor from '../../components/ContentEditor';
import type { ContentEditRequest } from '../../contexts/EditModeContext';
import {
  fetchController,
  updateController,
} from '../../lib/controllers-client';
import type { ControllerProduct } from '../../lib/controllers-client';

// Real production page components — same source, no duplicates
import HomePage               from '../HomePage';
import CustomControllersPage  from '../CustomControllersPage';
import AboutPage              from '../AboutPage';
import BookRepairPage         from '../BookRepairPage';
import BulkServicesPage       from '../BulkServicesPage';
import BuybackPage            from '../BuybackPage';
import CommunityPage          from '../CommunityPage';
import GalleryPage            from '../GalleryPage';
import MailInRepairsPage      from '../MailInRepairsPage';
import PerformanceUpgradesPage from '../PerformanceUpgradesPage';
import RetroModsPage          from '../RetroModsPage';
import ShopPage               from '../ShopPage';
import SupportPage            from '../SupportPage';

// ── Controller quick-edit panel ───────────────────────────────────────────────
function ControllerEditPanel({
  controllerId,
  onClose,
  onSaved,
}: {
  controllerId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [product, setProduct]   = useState<ControllerProduct | null>(null);
  const [fields,  setFields]    = useState<Partial<ControllerProduct>>({});
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [err,     setErr]       = useState<string | null>(null);
  const [saved,   setSaved]     = useState(false);

  React.useEffect(() => {
    setLoading(true);
    fetchController(controllerId)
      .then(p => { setProduct(p); setFields(p); })
      .catch(e => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, [controllerId]);

  const set = (k: keyof ControllerProduct, v: unknown) =>
    setFields(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!product) return;
    setSaving(true); setErr(null);
    try {
      await updateController(product.id, fields);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full bg-[#1a1a1a] border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#39FF14]";
  const lbl = "block text-[10px] font-bold uppercase tracking-widest text-[#B0B0B0] mb-1";

  return (
    <div className="fixed right-0 top-0 bottom-0 z-50 w-80 bg-[#111] border-l border-gray-800 flex flex-col shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 shrink-0">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-[#39FF14]">Edit Product</p>
          {product && <p className="text-sm font-semibold text-[#E0E0E0] mt-0.5 truncate">{product.name}</p>}
        </div>
        <button onClick={onClose} className="text-[#B0B0B0] hover:text-white p-1">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading && <p className="text-[#B0B0B0] text-sm text-center py-8">Loading…</p>}
        {err     && <p className="text-red-400 text-xs">{err}</p>}

        {!loading && product && (
          <>
            {/* Primary photo preview */}
            {(() => {
              const img = product.media?.find(m => m.is_primary)?.public_url ?? product.media?.[0]?.public_url;
              return img ? (
                <div className="aspect-square rounded-xl overflow-hidden bg-gray-900 border border-gray-800">
                  <img src={img} alt={product.name} className="w-full h-full object-cover" />
                </div>
              ) : null;
            })()}

            <div>
              <label className={lbl}>Name</label>
              <input className={inp} value={fields.name ?? ''} onChange={e => set('name', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Price ($)</label>
                <input className={inp} type="number" min={0} value={fields.price ?? 0} onChange={e => set('price', Number(e.target.value))} />
              </div>
              <div>
                <label className={lbl}>Compare At ($)</label>
                <input className={inp} type="number" min={0} placeholder="—" value={fields.compare_at_price ?? ''} onChange={e => set('compare_at_price', e.target.value ? Number(e.target.value) : null)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Status</label>
                <select className={inp} value={fields.status ?? 'active'} onChange={e => set('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
              <div>
                <label className={lbl}>Availability</label>
                <select className={inp} value={fields.availability ?? 'available'} onChange={e => set('availability', e.target.value)}>
                  <option value="available">Available</option>
                  <option value="sold">Sold Out</option>
                </select>
              </div>
            </div>
            <div>
              <label className={lbl}>Short Description</label>
              <input className={inp} value={fields.short_description ?? ''} onChange={e => set('short_description', e.target.value)} placeholder="One-line tagline" />
            </div>

            <a
              href="/admin/controllers"
              target="_blank"
              className="block text-center text-xs text-[#00BFFF] hover:underline pt-1"
            >
              Full editor (photos, ordering) ↗
            </a>
          </>
        )}
      </div>

      {/* Footer */}
      {!loading && product && (
        <div className="shrink-0 px-4 py-3 border-t border-gray-800 space-y-2">
          {err && <p className="text-red-400 text-xs">{err}</p>}
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] text-sm font-semibold transition-all">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all disabled:opacity-60 ${saved ? 'bg-[#39FF14]/20 text-[#39FF14]' : 'bg-[#39FF14] hover:bg-[#00BFFF] text-black'}`}
            >
              {saved ? 'Saved!' : saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page registry ─────────────────────────────────────────────────────────────

const PAGES: { id: string; label: string; Component: React.ComponentType }[] = [
  { id: 'home',                  label: 'Home',                Component: HomePage               },
  { id: 'custom-controllers',    label: 'Custom Controllers',  Component: CustomControllersPage  },
  { id: 'performance-upgrades',  label: 'Performance',         Component: PerformanceUpgradesPage },
  { id: 'retro-mods',            label: 'Retro Mods',          Component: RetroModsPage          },
  { id: 'shop',                  label: 'Shop',                Component: ShopPage               },
  { id: 'gallery',               label: 'Gallery',             Component: GalleryPage            },
  { id: 'book-repair',           label: 'Book Repair',         Component: BookRepairPage         },
  { id: 'bulk-services',         label: 'Bulk / B2B',          Component: BulkServicesPage       },
  { id: 'buyback',               label: 'Buyback',             Component: BuybackPage            },
  { id: 'mail-in',               label: 'Mail-In',             Component: MailInRepairsPage      },
  { id: 'community',             label: 'Community',           Component: CommunityPage          },
  { id: 'about',                 label: 'About',               Component: AboutPage              },
  { id: 'support',               label: 'Support',             Component: SupportPage            },
  { id: 'media-library',        label: '📁 Media Library',    Component: MediaLibraryPanel      },
];

type PageId = string;

// ── Preview widths ────────────────────────────────────────────────────────────

const PREVIEW_WIDTHS = {
  desktop: { label: 'Desktop', width: '100%',  Icon: Monitor    },
  tablet:  { label: 'Tablet',  width: '768px', Icon: Tablet     },
  mobile:  { label: 'Mobile',  width: '390px', Icon: Smartphone },
} as const;

type PreviewSize = keyof typeof PREVIEW_WIDTHS;

// ── Editor content ────────────────────────────────────────────────────────────

function EditorContent() {
  const [pageId,      setPageId]      = useState<PageId>('home');
  const [preview,     setPreview]     = useState<PreviewSize>('desktop');
  const [editingSlot,       setEditingSlot]       = useState<ImageSlot | null>(null);
  const [editingContent,    setEditingContent]    = useState<ContentEditRequest | null>(null);
  const [editingController, setEditingController] = useState<string | null>(null);
  const [saving,         setSaving]         = useState(false);
  const [saveError,      setSaveError]      = useState<string | null>(null);

  const handleEditController = useCallback((id: string) => {
    setEditingSlot(null);
    setEditingContent(null);
    setEditingController(id);
  }, []);

  const activePage  = PAGES.find(p => p.id === pageId)!;
  const PageComponent = activePage.Component;

  // Called by ImageSlot's edit button
  const handleEditSlot = (slotId: string, currentSlot: ImageSlot | null, fallbackSrc: string) => {
    setSaveError(null);
    const slot: ImageSlot = currentSlot ?? {
      slot_id:        slotId,
      page_section:   '',
      label:          slotId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      image_url:      fallbackSrc,
      alt_text:       '',
      focal_x:        50,
      focal_y:        50,
      zoom:           1,
      aspect_ratio:   'auto',
      object_fit:     'cover',
      container_size: 'auto',
      updated_at:     new Date().toISOString(),
    };
    setEditingSlot(slot);
  };

  const handleEditorSave = async (updates: Partial<Omit<ImageSlot, 'slot_id' | 'updated_at'>>) => {
    if (!editingSlot) return;
    setSaving(true);
    setSaveError(null);
    try {
      let finalUpdates = { ...updates };

      // If the user picked a local file (blob URL), upload it to Supabase Storage first
      if (finalUpdates.image_url?.startsWith('blob:')) {
        const res  = await fetch(finalUpdates.image_url);
        const blob = await res.blob();
        const file = new File([blob], 'upload.jpg', { type: blob.type || 'image/jpeg' });
        finalUpdates.image_url = await uploadImageFile(editingSlot.slot_id, file);
      }

      await saveImageSlot(editingSlot.slot_id, finalUpdates);
      // saveImageSlot calls invalidateSlotCache internally, which notifies all
      // useImageSlots hook instances to re-fetch — no page remount needed
      setEditingSlot(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleContentSave = async (content: string) => {
    if (!editingContent) return;
    await saveContentSlot(
      editingContent.slotId,
      content,
      editingContent.contentType as ContentType,
      editingContent.pageSection,
      editingContent.label,
    );
    setEditingContent(null);
  };

  const handleContentReset = async () => {
    if (!editingContent) return;
    await deleteContentSlot(editingContent.slotId);
    setEditingContent(null);
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0] flex flex-col">
      <SEOHead title="Edit Site | GameFix Admin" description="" noindex />

      {/* ── Admin toolbar ── */}
      <header className="shrink-0 bg-gray-900 border-b border-gray-800 px-4 py-2.5 flex items-center gap-3 flex-wrap z-40">
        <Link
          to="/admin/services"
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#E0E0E0] transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Admin
        </Link>
        <div className="h-4 w-px bg-gray-700" />
        <span className="text-xs font-black uppercase tracking-widest text-[#39FF14]">Edit Site</span>
        <div className="h-4 w-px bg-gray-700" />

        {/* Page selector */}
        <select
          value={pageId}
          onChange={e => setPageId(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-[#E0E0E0] text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#39FF14] cursor-pointer"
        >
          {PAGES.map(p => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>

        {/* Preview width */}
        <div className="ml-auto flex items-center gap-1">
          {(Object.entries(PREVIEW_WIDTHS) as [PreviewSize, typeof PREVIEW_WIDTHS[PreviewSize]][]).map(([key, val]) => {
            const Icon = val.Icon;
            return (
              <button
                key={key}
                onClick={() => setPreview(key)}
                title={val.label}
                className={`p-1.5 rounded-lg transition-colors ${
                  preview === key
                    ? 'bg-[#39FF14]/15 text-[#39FF14]'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
              </button>
            );
          })}
          <span className="text-xs text-gray-600 ml-1">{PREVIEW_WIDTHS[preview].label}</span>
        </div>
      </header>

      {/* ── Edit mode notice ── */}
      <div className="shrink-0 bg-[#39FF14]/5 border-b border-[#39FF14]/20 px-4 py-1.5 flex items-center gap-2 text-xs text-[#39FF14]/70">
        <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse shrink-0" />
        Edit Mode — hover over images to reveal edit controls. Changes save directly to Supabase and go live immediately.
        {saveError && (
          <span className="ml-auto text-red-400 font-semibold">Save failed: {saveError}</span>
        )}
      </div>

      {/* ── Page preview ── */}
      <div className="flex-1 overflow-y-auto bg-[#1c1c1c]">
        <div
          style={{
            width:    PREVIEW_WIDTHS[preview].width,
            maxWidth: '100%',
            margin:   '0 auto',
          }}
          className="bg-[#0D0D0D] min-h-full shadow-2xl"
        >
          {pageId === 'media-library' ? (
            <PageComponent />
          ) : (
            <EditModeContext.Provider
              value={{
                active: true,
                onEditSlot: handleEditSlot,
                onEditContent: req => setEditingContent(req),
                onEditController: handleEditController,
              }}
            >
              <PageComponent />
            </EditModeContext.Provider>
          )}
        </div>

        {/* Preview width indicator shown in tablet/mobile modes */}
        {preview !== 'desktop' && (
          <div className="text-center py-3 text-xs text-gray-600">
            {PREVIEW_WIDTHS[preview].label} — {PREVIEW_WIDTHS[preview].width} container
            {' '}(Tailwind breakpoints are viewport-relative; use browser DevTools for exact responsive preview)
          </div>
        )}
      </div>

      {/* ── ContentEditor modal ── */}
      {editingContent && (
        <ContentEditor
          request={editingContent}
          onSave={handleContentSave}
          onReset={handleContentReset}
          onClose={() => setEditingContent(null)}
        />
      )}

      {/* ── Controller quick-edit panel ── */}
      {editingController && (
        <ControllerEditPanel
          controllerId={editingController}
          onClose={() => setEditingController(null)}
          onSaved={() => setEditingController(null)}
        />
      )}

      {/* ── ImageEditor modal ── */}
      {editingSlot && (
        <div className="fixed inset-0 z-50">
          <ImageEditor
            slot={editingSlot}
            onSave={handleEditorSave}
            onClose={() => { setEditingSlot(null); setSaveError(null); }}
            onEditGallerySlot={slot => { setSaveError(null); setEditingSlot(slot); }}
          />
          {saving && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <div className="bg-gray-900 border border-gray-700 rounded-xl px-6 py-4 text-sm text-[#39FF14] font-semibold">
                Uploading & saving…
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page entry point (auth wrapper) ──────────────────────────────────────────

export default function AdminEditSitePage() {
  const [authed, setAuthed] = useState(isAdminAuthed());
  return authed
    ? <EditorContent />
    : <AdminLoginGate onAuth={() => setAuthed(true)} />;
}
